import { useId, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type TooltipProps = {
  content: string
  children: ReactNode
  side?: 'top' | 'bottom' | 'right'
  /** ms before showing on hover. Focus always shows immediately. */
  delay?: number
  className?: string
  /** Applied to the wrapper around `children`, not the bubble. */
  triggerClassName?: string
}

const SIDE_CLASSES: Record<NonNullable<TooltipProps['side']>, string> = {
  top: 'bottom-[calc(100%+0.375rem)] left-1/2 -translate-x-1/2',
  bottom: 'top-[calc(100%+0.375rem)] left-1/2 -translate-x-1/2',
  right: 'left-[calc(100%+0.5rem)] top-1/2 -translate-y-1/2',
}

/**
 * Hover *and* focus triggered, `role="tooltip"`, wired with `aria-describedby`.
 * A tooltip is never the only source of a control's name — icon buttons still
 * carry their own `aria-label`.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  delay = 250,
  className,
  triggerClassName,
}: TooltipProps): ReactNode {
  const [visible, setVisible] = useState(false)
  const timer = useRef<number | null>(null)
  const id = useId()

  const show = (immediate: boolean): void => {
    if (immediate) {
      setVisible(true)
      return
    }
    timer.current = window.setTimeout(() => setVisible(true), delay)
  }

  const hide = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
    setVisible(false)
  }

  return (
    <span
      className={cn('relative inline-flex', triggerClassName)}
      onPointerEnter={() => show(false)}
      onPointerLeave={hide}
      onFocusCapture={() => show(true)}
      onBlurCapture={hide}
      aria-describedby={visible ? id : undefined}
    >
      {children}
      {visible ? (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'pointer-events-none absolute z-50 w-max max-w-56 rounded-md px-2 py-1',
            'bg-neutral-900 text-caption text-neutral-50 shadow-2',
            SIDE_CLASSES[side],
            className,
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  )
}
